/**
 * RS cart drawer — toast, open on add, bundle upsell, cart toggle sync.
 * Owns a reliable drawer re-render after cart:change (Prestige section HTML from
 * cart/add.js is often stale vs item_count, especially with multi-item / packs).
 */
(function () {
  const TOAST_DURATION_MS = 2400;

  function getDrawer() {
    return document.getElementById("cart-drawer") || document.querySelector("cart-drawer.rs-cart-drawer");
  }

  function getCartToggle() {
    return document.getElementById("cart-toggle");
  }

  function getToast() {
    return document.querySelector("cart-drawer.rs-cart-drawer .cart-drawer__toast");
  }

  function shopRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function setToggleExpanded(expanded) {
    getCartToggle()?.setAttribute("aria-expanded", String(expanded));
  }

  function showToast() {
    const toast = getToast();
    if (!toast) return;

    toast.hidden = false;
    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => {
      toast.hidden = true;
    }, TOAST_DURATION_MS);
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.show !== "function") return;
    drawer.show();
  }

  function initCartToggle() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.addEventListener("dialog:after-show", () => setToggleExpanded(true));
    drawer.addEventListener("dialog:after-hide", () => setToggleExpanded(false));
    setToggleExpanded(Boolean(drawer.open));
  }

  function getCartDrawerSectionId() {
    const sections = [];
    document.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
    );

    if (sections.length) return sections[0];

    const drawer = getDrawer();
    const sectionEl = drawer?.closest("[id^='shopify-section-']");
    return sectionEl ? sectionEl.id.replace("shopify-section-", "") : null;
  }

  function getCartDrawerSectionIds() {
    const id = getCartDrawerSectionId();
    return id ? [id] : [];
  }

  /**
   * Parse section HTML into cart-drawer innerHTML.
   * Supports both Section Rendering API wrappers and bare cart-drawer markup.
   */
  function extractCartDrawerInnerHtml(html, sectionId) {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const wrapped =
      (sectionId && doc.getElementById(`shopify-section-${sectionId}`)?.querySelector("cart-drawer")) ||
      doc.querySelector("cart-drawer.rs-cart-drawer") ||
      doc.querySelector("cart-drawer");

    return wrapped ? wrapped.innerHTML : null;
  }

  async function fetchFreshDrawerHtml(sectionId) {
    const root = shopRoot();
    const bust = Date.now();

    // sections (plural) JSON API — usually most reliable with the cart cookie
    try {
      const jsonRes = await fetch(`${root}?sections=${encodeURIComponent(sectionId)}&_=${bust}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (jsonRes.ok) {
        const json = await jsonRes.json();
        if (json?.[sectionId]) return json[sectionId];
      }
    } catch {
      /* fall through */
    }

    const htmlRes = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}&_=${bust}`, {
      credentials: "same-origin",
    });
    return htmlRes.text();
  }

  async function refreshDrawerFromServer() {
    const drawer = getDrawer();
    const sectionId = getCartDrawerSectionId();
    if (!drawer || !sectionId) return false;

    try {
      const html = await fetchFreshDrawerHtml(sectionId);
      const inner = extractCartDrawerInnerHtml(html, sectionId);
      if (!inner) {
        console.error("RS cart drawer: could not parse refreshed section", sectionId);
        return false;
      }

      const fragment = document.createRange().createContextualFragment(inner);
      drawer.replaceChildren(...fragment.children);

      const cart = await (await fetch(`${shopRoot()}cart.js`, { credentials: "same-origin" })).json();
      drawer.classList.toggle("drawer--center-body", cart.item_count === 0);

      drawer.dispatchEvent(new CustomEvent("cart-drawer:refreshed", { bubbles: true }));
      return true;
    } catch (error) {
      console.error("RS cart drawer: refresh failed", error);
      return false;
    }
  }

  async function fetchCartDrawerSections(sectionIds) {
    const sections = {};
    await Promise.all(
      sectionIds.map(async (id) => {
        sections[id] = await fetchFreshDrawerHtml(id);
      })
    );
    return sections;
  }

  async function addVariantsToCart(variantIds) {
    const root = shopRoot();
    const sectionIds = getCartDrawerSectionIds();
    const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
    const payload = { items };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(",");
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        body: JSON.stringify(payload),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        throw new Error(responseJson?.description || responseJson?.message || "Add to cart failed");
      }

      const cartContent = await (await fetch(`${root}cart.js`, { credentials: "same-origin" })).json();
      cartContent.sections = sectionIds.length ? await fetchCartDrawerSections(sectionIds) : {};

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            onSuccessDo: "force_open_drawer",
            cart: cartContent,
          },
        })
      );
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      console.error("RS cart drawer: bundle add failed", error);
    }
  }

  /**
   * After Prestige tries (and often fails) to swap drawer HTML from cart/add.js
   * sections, force a fresh Section Rendering fetch and open the drawer.
   */
  let refreshSeq = 0;
  async function onVariantAdded() {
    const seq = ++refreshSeq;
    showToast();

    // Let Prestige's sync/microtask replace attempt run first, then overwrite.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (seq !== refreshSeq) return;

    await refreshDrawerFromServer();
    if (seq !== refreshSeq) return;

    openDrawer();

    // Automatic discounts (e.g. 2BEZELPACKAGE) can settle slightly after add.
    window.setTimeout(async () => {
      if (seq !== refreshSeq) return;
      await refreshDrawerFromServer();
    }, 400);
  }

  document.addEventListener("cart:change", (event) => {
    if (event.detail?.baseEvent !== "variant:add") return;
    onVariantAdded();
  });

  document.addEventListener("cart-drawer:refreshed", () => {
    const toast = getToast();
    if (toast && !toast.hidden) {
      showToast();
    }
  });

  document.addEventListener("click", (event) => {
    const bundleBtn = event.target.closest("[data-rs-cart-bundle]");
    if (!bundleBtn) return;

    event.preventDefault();
    const variantIds = (bundleBtn.dataset.variantIds || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    if (!variantIds.length) return;

    bundleBtn.setAttribute("aria-busy", "true");
    bundleBtn.disabled = true;

    addVariantsToCart(variantIds).finally(() => {
      bundleBtn.removeAttribute("aria-busy");
      bundleBtn.disabled = false;
    });
  });

  function init() {
    initCartToggle();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Expose for PDP scripts that want the same refresh path
  window.RsCartDrawer = {
    refresh: refreshDrawerFromServer,
    open: openDrawer,
  };
})();
